import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle, MailX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { unsubscribeFromCampaign } from "@/api/campaign.js";

const STATUS = { LOADING: "loading", SUCCESS: "success", ERROR: "error", NO_TOKEN: "no_token" };

export default function UnsubscribePage() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const [status, setStatus] = useState(token ? STATUS.LOADING : STATUS.NO_TOKEN);
    const calledRef = useRef(false);

    useEffect(() => {
        if (!token || calledRef.current) return;
        calledRef.current = true;

        unsubscribeFromCampaign(token)
            .then(() => setStatus(STATUS.SUCCESS))
            .catch(() => setStatus(STATUS.ERROR));
    }, [token]);

    const content = {
        [STATUS.LOADING]: {
            icon: <Loader2 className="h-8 w-8 text-slate-300 animate-spin" />,
            iconBg: "bg-white/5",
            title: t("unsubscribe.loading_title", "Désinscription en cours…"),
            desc: t("unsubscribe.loading_desc", "Un instant, nous traitons votre demande."),
        },
        [STATUS.SUCCESS]: {
            icon: <CheckCircle2 className="h-8 w-8 text-emerald-400" />,
            iconBg: "bg-emerald-500/10",
            title: t("unsubscribe.success_title", "Vous êtes désinscrit"),
            desc: t("unsubscribe.success_desc", "Vous ne recevrez plus d'emails de relance de cet établissement. Vous pouvez fermer cette page."),
        },
        [STATUS.ERROR]: {
            icon: <AlertTriangle className="h-8 w-8 text-red-400" />,
            iconBg: "bg-red-500/10",
            title: t("unsubscribe.error_title", "Lien invalide ou expiré"),
            desc: t("unsubscribe.error_desc", "Nous n'avons pas pu traiter votre désinscription. Le lien a peut-être déjà été utilisé."),
        },
        [STATUS.NO_TOKEN]: {
            icon: <AlertTriangle className="h-8 w-8 text-red-400" />,
            iconBg: "bg-red-500/10",
            title: t("unsubscribe.error_title", "Lien invalide ou expiré"),
            desc: t("unsubscribe.no_token_desc", "Ce lien de désinscription est incomplet."),
        },
    }[status];

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0B0F19] px-5 font-sans">
            <div className="w-full max-w-sm bg-white/4 border border-white/10 rounded-3xl p-8 text-center shadow-2xl">
                <div className="flex items-center justify-center mb-8 text-white font-black text-xl tracking-tight">
                    GOJO<span className="text-amber-400">.</span>
                </div>

                <div className={`mx-auto mb-5 h-16 w-16 rounded-2xl flex items-center justify-center ${content.iconBg}`}>
                    {content.icon}
                </div>

                <h1 className="text-xl font-black text-white mb-2">{content.title}</h1>
                <p className="text-sm text-slate-400 leading-relaxed">{content.desc}</p>

                <div className="mt-8 flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-600">
                    <MailX className="h-3.5 w-3.5" /> {t("unsubscribe.footer", "Préférences email")}
                </div>
            </div>
        </div>
    );
}
